import { ElementProps } from '@/components/sections/builder/types';
import { ScrollingTextElementDataType } from '@/components/sections/builder/elements/body/scrolling-text/types';
import { WrapperChild } from '@/components/sections/builder/wrapper';

import clsx from 'clsx';
import { useEffect, useState } from 'react';

export default function ElementContent({ element, preview }: ElementProps) {
  const [duplicateChild, setDuplicateChild] = useState(1);
  const [useIOSFix, setUseIOSFix] = useState(false);

  const childs = element.childs || [];
  const data = element.data as ScrollingTextElementDataType;

  const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari =
    typeof window !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  useEffect(() => {
    if (isIOS || isSafari) {
      setUseIOSFix(true);
    }
  }, [isIOS, isSafari]);

  useEffect(() => {
    const parentWidth = document.getElementById(`scrolling-parent-${element.id}`)?.offsetWidth || 0;
    const rawWrapperWidth =
      document.getElementById(`scrolling-wrapper-${element.id}`)?.offsetWidth || 0;
    const wrapperWidth = rawWrapperWidth / duplicateChild;

    if (parentWidth > wrapperWidth && wrapperWidth > 0) {
      setDuplicateChild(Math.ceil(parentWidth / wrapperWidth));
    } else {
      setDuplicateChild(1);
    }
  }, [childs]);

  const isDuplicateChild = Math.max(1, duplicateChild || 1);
  const animationDuration = data?.duration ? `${data.duration}s` : '10s';

  return (
    <>
      <div className='mx-auto w-full duration-100'>
        <div className='padding-left-wrapper padding-right-wrapper'>
          <div
            id={`scrolling-parent-${element.id}`}
            className={clsx(
              'relative w-full overflow-x-hidden bg-white',
              data?.separator && 'border-b',
              data?.fontSize === 'small' && 'text-sm md:text-base',
              data?.fontSize === 'medium' && 'text-base md:text-lg',
              data?.fontSize === 'large' && 'text-lg md:text-2xl',
              data?.colorSchemeScrollingText && 'bg_section'
            )}
            style={
              {
                '--animation-duration': animationDuration,
              } as React.CSSProperties
            }
          >
            <div
              id={`scrolling-wrapper-${element.id}`}
              className={clsx(
                'scrolling-text-padding flex w-fit whitespace-nowrap py-4 [&>*]:mr-4',
                useIOSFix && 'ios-scroll-fix',
                data?.direction === 'left' &&
                  (useIOSFix ? 'animate-left-marquee-ios' : 'animate-left-marquee'),
                data?.direction === 'right' &&
                  (useIOSFix ? 'animate-right-marquee-ios' : 'animate-right-marquee')
              )}
            >
              {[...new Array(isDuplicateChild).fill(0)].map((_, n) => {
                return childs.map((child) => (
                  <WrapperChild
                    key={`${child.id}-${n}`}
                    child={child}
                    parentType={element.type}
                    preview={preview}
                  />
                ));
              })}
            </div>

            <div
              className={clsx(
                'scrolling-text-padding absolute top-0 flex whitespace-nowrap py-4 [&>*]:mr-4',
                useIOSFix && 'ios-scroll-fix',
                data?.direction === 'left' &&
                  (useIOSFix ? 'animate-left-marquee2-ios' : 'animate-left-marquee2'),
                data?.direction === 'right' &&
                  (useIOSFix ? 'animate-right-marquee2-ios' : 'animate-right-marquee2')
              )}
            >
              {[...new Array(duplicateChild).fill(0)].map((_, n) => {
                return childs.map((child) => (
                  <WrapperChild
                    key={`${child.id}-${n}`}
                    child={child}
                    parentType={element.type}
                    preview={preview}
                  />
                ));
              })}
            </div>
          </div>
        </div>
      </div>
      <style jsx>
        {`
          /* Original Animations */
          @keyframes leftMarquee2 {
            0% {
              transform: translateX(100%);
            }
            100% {
              transform: translateX(0%);
            }
          }
          .animate-left-marquee2 {
            animation: leftMarquee2 var(--animation-duration, 10s) linear infinite;
          }

          @keyframes leftMarquee {
            0% {
              transform: translateX(0%);
            }
            100% {
              transform: translateX(-100%);
            }
          }
          .animate-left-marquee {
            animation: leftMarquee var(--animation-duration, 10s) linear infinite;
          }

          @keyframes rightMarquee2 {
            0% {
              transform: translateX(0%);
            }
            100% {
              transform: translateX(100%);
            }
          }
          .animate-right-marquee2 {
            animation: rightMarquee2 var(--animation-duration, 10s) linear infinite;
          }

          @keyframes rightMarquee {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(0%);
            }
          }
          .animate-right-marquee {
            animation: rightMarquee var(--animation-duration, 10s) linear infinite;
          }

          /* iOS Fixed Animations */
          .ios-scroll-fix {
            -webkit-transform: translate3d(0, 0, 0);
            transform: translate3d(0, 0, 0);
            will-change: transform;
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
          }

          @keyframes leftMarquee2iOS {
            0% {
              -webkit-transform: translate3d(100%, 0, 0);
              transform: translate3d(100%, 0, 0);
            }
            100% {
              -webkit-transform: translate3d(0%, 0, 0);
              transform: translate3d(0%, 0, 0);
            }
          }
          @-webkit-keyframes leftMarquee2iOS {
            0% {
              -webkit-transform: translate3d(100%, 0, 0);
            }
            100% {
              -webkit-transform: translate3d(0%, 0, 0);
            }
          }
          .animate-left-marquee2-ios {
            -webkit-animation: leftMarquee2iOS var(--animation-duration, 10s) linear infinite;
            animation: leftMarquee2iOS var(--animation-duration, 10s) linear infinite;
          }

          @keyframes leftMarqueeIOS {
            0% {
              -webkit-transform: translate3d(0%, 0, 0);
              transform: translate3d(0%, 0, 0);
            }
            100% {
              -webkit-transform: translate3d(-100%, 0, 0);
              transform: translate3d(-100%, 0, 0);
            }
          }
          @-webkit-keyframes leftMarqueeIOS {
            0% {
              -webkit-transform: translate3d(0%, 0, 0);
            }
            100% {
              -webkit-transform: translate3d(-100%, 0, 0);
            }
          }
          .animate-left-marquee-ios {
            -webkit-animation: leftMarqueeIOS var(--animation-duration, 10s) linear infinite;
            animation: leftMarqueeIOS var(--animation-duration, 10s) linear infinite;
          }

          @keyframes rightMarquee2iOS {
            0% {
              -webkit-transform: translate3d(0%, 0, 0);
              transform: translate3d(0%, 0, 0);
            }
            100% {
              -webkit-transform: translate3d(100%, 0, 0);
              transform: translate3d(100%, 0, 0);
            }
          }
          @-webkit-keyframes rightMarquee2iOS {
            0% {
              -webkit-transform: translate3d(0%, 0, 0);
            }
            100% {
              -webkit-transform: translate3d(100%, 0, 0);
            }
          }
          .animate-right-marquee2-ios {
            -webkit-animation: rightMarquee2iOS var(--animation-duration, 10s) linear infinite;
            animation: rightMarquee2iOS var(--animation-duration, 10s) linear infinite;
          }

          @keyframes rightMarqueeIOS {
            0% {
              -webkit-transform: translate3d(-100%, 0, 0);
              transform: translate3d(-100%, 0, 0);
            }
            100% {
              -webkit-transform: translate3d(0%, 0, 0);
              transform: translate3d(0%, 0, 0);
            }
          }
          @-webkit-keyframes rightMarqueeIOS {
            0% {
              -webkit-transform: translate3d(-100%, 0, 0);
            }
            100% {
              -webkit-transform: translate3d(0%, 0, 0);
            }
          }
          .animate-right-marquee-ios {
            -webkit-animation: rightMarqueeIOS var(--animation-duration, 10s) linear infinite;
            animation: rightMarqueeIOS var(--animation-duration, 10s) linear infinite;
          }

          @media (min-width: 768px) {
            .scrolling-text-padding {
              padding-top: ${data?.padding?.top || 0}px;
              padding-bottom: ${data?.padding?.bottom || 0}px;
            }
          }

          .bg_section {
            background-color: ${data?.colorSchemeScrollingText?.background || 'white'};
            color: ${data?.colorSchemeScrollingText?.text || 'black'};
          }

          .padding-left-wrapper {
            padding-left: ${data?.padding?.left || 0}px;
            background-color: ${data?.colorSchemeScrollingText?.background || 'white'};
          }

          .padding-right-wrapper {
            padding-right: ${data?.padding?.right || 0}px;
            background-color: ${data?.colorSchemeScrollingText?.background || 'white'};
          }
        `}
      </style>
    </>
  );
}
